import React, { ReactElement } from 'react';
import { AdminActivity, LoginActivity, MarketplaceActivity, PostActivity, UserActivity } from '../models/AdminActivity';
import { 
  MessageSquare, Edit, Trash2, EyeOff, Eye, Heart, Plus, ShieldOff, Shield, 
  Users, MessageCircle, TrendingUp, AlertTriangle 
} from 'lucide-react';

import { getCurrentTimezone, getCurrentTimeFormat, getLocale } from '../config/settings';
import { formatDate, formatTime } from './dateUtils';

// Formatting utilities
export const formatTimestamp = (timestamp: string): string => {
  // Use centralized date formatting for yyyy-mm-dd format
  const datePart = formatDate(timestamp);
  const timePart = formatTime(timestamp);
  
  return `${datePart} ${timePart}`;
};

export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export const formatActivityAction = (activity: AdminActivity): string => {
  switch (activity.type) {
    case 'login':
      return activity.success ? 'Successful Login' : `Failed Login: ${activity.failureReason || 'Unknown error'}`;
    
    case 'marketplace':
      const marketplaceActions = {
        create_sale_offer: 'Created Sale Offer',
        create_buy_request: 'Created Buy Request',
        delete_sale_offer: 'Deleted Sale Offer',
        delete_buy_request: 'Deleted Buy Request',
        contact_buyer: 'Contacted Buyer',
        contact_seller: 'Contacted Seller'
      };
      return marketplaceActions[activity.action] || activity.action;
    
    case 'post':
      const postActions = {
        create: 'Created Post',
        edit: 'Edited Post',
        delete: 'Deleted Post',
        hide: 'Hidden Post',
        show: 'Showed Post',
        react: `Reacted with ${activity.details.reactionType || 'unknown'}`
      };
      return postActions[activity.action] || activity.action;
    
    case 'user':
      const userActions = {
        create: 'Created User',
        update: 'Updated User',
        delete: 'Deleted User',
        suspend: 'Suspended User',
        unsuspend: 'Unsuspended User',
        promote_admin: 'Promoted to Admin',
        demote_admin: 'Demoted from Admin',
        promote_super_admin: 'Promoted to Super Admin',
        demote_super_admin: 'Demoted from Super Admin'
      };
      return userActions[activity.action] || activity.action;
    
    default:
      return 'Unknown Action';
  }
};

export const getActivityIcon = (activity: AdminActivity): string => {
  switch (activity.type) {
    case 'login':
      return activity.success ? '🔐' : '❌';
    case 'marketplace':
      return '💼';
    case 'post':
      return '📝';
    case 'user':
      return '👤';
    default:
      return '❓';
  }
};

export const getActivityColor = (activity: AdminActivity): string => {
  switch (activity.type) {
    case 'login':
      return activity.success ? 'text-green-600' : 'text-red-600';
    case 'marketplace':
      return 'text-blue-600';
    case 'post':
      return 'text-purple-600';
    case 'user':
      return 'text-orange-600';
    default:
      return 'text-gray-600';
  }
};

export const getActivityDetails = (activity: AdminActivity): string => {
  switch (activity.type) {
    case 'login':
      const loginActivity = activity as LoginActivity;
      if (loginActivity.success && loginActivity.sessionDuration) {
        return `Session: ${formatDuration(loginActivity.sessionDuration)}`;
      }
      return loginActivity.failureReason || 'No details';
    
    case 'marketplace':
      const marketplaceActivity = activity as MarketplaceActivity;
      const details = [];
      if (marketplaceActivity.details.sharePrice) {
        details.push(`$${marketplaceActivity.details.sharePrice}`);
      }
      if (marketplaceActivity.details.numberOfShares) {
        details.push(`${marketplaceActivity.details.numberOfShares} shares`);
      }
      if (marketplaceActivity.details.contactedUserName) {
        details.push(`Contacted: ${marketplaceActivity.details.contactedUserName}`);
      }
      return details.length > 0 ? details.join(', ') : `${marketplaceActivity.entityType} ${marketplaceActivity.entityId}`;
    
    case 'post':
      const postActivity = activity as PostActivity;
      if (postActivity.postTitle) {
        return `"${postActivity.postTitle}"`;
      }
      if (postActivity.details.editedFields) {
        return `Edited: ${postActivity.details.editedFields.join(', ')}`;
      }
      return `${postActivity.postType} post ${postActivity.postId}`;
    
    case 'user':
      const userActivity = activity as UserActivity;
      let details_str = `Target: ${userActivity.targetUserName}`;
      if (userActivity.details.changedFields) {
        details_str += ` (${userActivity.details.changedFields.join(', ')})`;
      }
      return details_str;
    
    default:
      return 'No details available';
  }
};

// Pagination utilities
export const generatePageNumbers = (currentPage: number, totalPages: number): number[] => {
  const delta = 2; // Number of pages to show on each side of current page
  const range = [];
  const rangeWithDots = [];

  for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
    range.push(i);
  }

  if (currentPage - delta > 2) {
    rangeWithDots.push(1, -1); // -1 represents dots
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage + delta < totalPages - 1) {
    rangeWithDots.push(-1, totalPages); // -1 represents dots
  } else if (totalPages > 1) {
    rangeWithDots.push(totalPages);
  }

  return rangeWithDots;
};

// Filtering utilities
export const getUniqueUsers = (activities: AdminActivity[]): Array<{id: string, name: string}> => {
  const userMap = new Map<string, string>();
  
  activities.forEach(activity => {
    userMap.set(activity.userId, activity.userName);
    
    // Also add target users from user activities
    if (activity.type === 'user') {
      const userActivity = activity as UserActivity;
      userMap.set(userActivity.targetUserId, userActivity.targetUserName);
    }
    
    // Also add contacted users from marketplace activities
    if (activity.type === 'marketplace') {
      const marketplaceActivity = activity as MarketplaceActivity;
      if (marketplaceActivity.details.contactedUserId && marketplaceActivity.details.contactedUserName) {
        userMap.set(marketplaceActivity.details.contactedUserId, marketplaceActivity.details.contactedUserName);
      }
    }
  });
  
  return Array.from(userMap.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const getUniqueActions = (activities: AdminActivity[]): string[] => {
  const actions = new Set<string>();
  
  activities.forEach(activity => {
    if ('action' in activity) {
      actions.add(activity.action);
    }
  });
  
  return Array.from(actions).sort();
};

// Date utilities
export const formatDateForInput = (date: Date): string => {
  const timezone = getCurrentTimezone();
  const locale = getLocale();
  return date.toLocaleDateString(locale, { timeZone: timezone });
};

export const formatDateForFilename = (date: Date): string => {
  const timezone = getCurrentTimezone();
  const locale = getLocale();
  return date.toLocaleDateString(locale, { timeZone: timezone });
};

export const getDateRangePresets = () => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  const lastWeek = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
  return {
    today: { from: formatDateForInput(today), to: formatDateForInput(now) },
    yesterday: { from: formatDateForInput(yesterday), to: formatDateForInput(yesterday) },
    lastWeek: { from: formatDateForInput(lastWeek), to: formatDateForInput(now) },
    lastMonth: { from: formatDateForInput(lastMonth), to: formatDateForInput(now) }
  };
};

// Export utilities
export const exportToCSV = (activities: AdminActivity[], filename: string): void => {
  const headers = ['Timestamp', 'Type', 'User', 'Action', 'Details', 'IP Address', 'Location'];
  const rows = activities.map(activity => {
    const location = activity.location ? 
      `${activity.location.city}, ${activity.location.region}, ${activity.location.country}` : 
      'Unknown';
    
    return [
      formatTimestamp(activity.timestamp),
      activity.type,
      activity.userName,
      formatActivityAction(activity),
      getActivityDetails(activity),
      activity.ipAddress || 'Unknown',
      location
    ];
  });
  
  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Search utilities
export const searchActivities = (activities: AdminActivity[], searchTerm: string): AdminActivity[] => {
  if (!searchTerm.trim()) return activities;
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return activities.filter(activity => {
    // Search in basic fields
    if (activity.userName.toLowerCase().includes(lowerSearchTerm) ||
        (activity.ipAddress && activity.ipAddress.toLowerCase().includes(lowerSearchTerm)) ||
        formatActivityAction(activity).toLowerCase().includes(lowerSearchTerm) ||
        getActivityDetails(activity).toLowerCase().includes(lowerSearchTerm)) {
      return true;
    }
    
    // Search in location data
    if (activity.location) {
      const locationString = `${activity.location.city} ${activity.location.region} ${activity.location.country}`.toLowerCase();
      if (locationString.includes(lowerSearchTerm)) {
        return true;
      }
    }
    
    // Search in type-specific fields
    switch (activity.type) {
      case 'marketplace':
        const marketplaceActivity = activity as MarketplaceActivity;
        return marketplaceActivity.entityId.toLowerCase().includes(lowerSearchTerm) ||
               (marketplaceActivity.details.contactedUserName?.toLowerCase().includes(lowerSearchTerm));
      
      case 'post':
        const postActivity = activity as PostActivity;
        return postActivity.postId.toLowerCase().includes(lowerSearchTerm) ||
               (postActivity.postTitle?.toLowerCase().includes(lowerSearchTerm));
      
      case 'user':
        const userActivity = activity as UserActivity;
        return userActivity.targetUserName.toLowerCase().includes(lowerSearchTerm) ||
               userActivity.targetUserId.toLowerCase().includes(lowerSearchTerm);
      
      default:
        return false;
    }
  });
};

// Shared action icon mapping for admin activity components
export const getActionIcon = (action: string, context: 'post' | 'user' | 'marketplace' | 'login' = 'post'): ReactElement => {
  switch (context) {
    case 'post':
      switch (action) {
        case 'create': return React.createElement(MessageSquare, { className: "w-4 h-4" });
        case 'edit': return React.createElement(Edit, { className: "w-4 h-4" });
        case 'delete': return React.createElement(Trash2, { className: "w-4 h-4" });
        case 'hide': return React.createElement(EyeOff, { className: "w-4 h-4" });
        case 'show': return React.createElement(Eye, { className: "w-4 h-4" });
        case 'react': return React.createElement(Heart, { className: "w-4 h-4" });
        default: return React.createElement(MessageSquare, { className: "w-4 h-4" });
      }
    
    case 'user':
      switch (action) {
        case 'create': return React.createElement(Plus, { className: "w-4 h-4" });
        case 'update': return React.createElement(Edit, { className: "w-4 h-4" });
        case 'delete': return React.createElement(Trash2, { className: "w-4 h-4" });
        case 'suspend':
        case 'unsuspend': return React.createElement(ShieldOff, { className: "w-4 h-4" });
        case 'promote_admin':
        case 'promote_super_admin':
        case 'demote_admin':
        case 'demote_super_admin': return React.createElement(Shield, { className: "w-4 h-4" });
        default: return React.createElement(Users, { className: "w-4 h-4" });
      }
    
    case 'marketplace':
      switch (action) {
        case 'create_sale_offer':
        case 'create_buy_request': return React.createElement(Plus, { className: "w-4 h-4" });
        case 'delete_sale_offer':
        case 'delete_buy_request': return React.createElement(Trash2, { className: "w-4 h-4" });
        case 'contact_buyer':
        case 'contact_seller': return React.createElement(MessageCircle, { className: "w-4 h-4" });
        default: return React.createElement(TrendingUp, { className: "w-4 h-4" });
      }
    
    case 'login':
      return React.createElement(Shield, { className: "w-4 h-4" });
    
    default:
      return React.createElement(MessageSquare, { className: "w-4 h-4" });
  }
};

// Shared action color mapping for admin activity components  
export const getActionColor = (action: string, context: 'post' | 'user' | 'marketplace' | 'login' = 'post'): string => {
  switch (context) {
    case 'post':
      switch (action) {
        case 'create': return 'bg-green-100 text-green-800';
        case 'edit': return 'bg-blue-100 text-blue-800';
        case 'delete': return 'bg-red-100 text-red-800';
        case 'hide': return 'bg-orange-100 text-orange-800';
        case 'show': return 'bg-green-100 text-green-800';
        case 'react': return 'bg-pink-100 text-pink-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    
    case 'user':
      switch (action) {
        case 'create': return 'bg-green-100 text-green-800';
        case 'update': return 'bg-blue-100 text-blue-800';
        case 'delete': return 'bg-red-100 text-red-800';
        case 'suspend': return 'bg-orange-100 text-orange-800';
        case 'unsuspend': return 'bg-green-100 text-green-800';
        case 'promote_admin':
        case 'promote_super_admin': return 'bg-purple-100 text-purple-800';
        case 'demote_admin':
        case 'demote_super_admin': return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    
    case 'marketplace':
      switch (action) {
        case 'create_sale_offer':
        case 'create_buy_request': return 'bg-green-100 text-green-800';
        case 'delete_sale_offer':
        case 'delete_buy_request': return 'bg-red-100 text-red-800';
        case 'contact_buyer':
        case 'contact_seller': return 'bg-blue-100 text-blue-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    
    default:
      return 'bg-gray-100 text-gray-800';
  }
}; 